import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import images from "../../assets/images";
import { useTranslate } from "../../context/TranslationContext";
import { useGetAllTemplatesQuery } from "../../redux/Apis/Template";
import { useUpdateUserMutation } from "../../redux/Apis/User";
import HeroSectionTemplateCard from "../cards/HeroSectionTemplateCard";
import OwlCarousel from "../carousel";
import SwitchToBothRoleModal from "../modal/SwitchToBothRoleModal";
import SkeletonCourseCard from "./SkeletonCourseCard";

const TemplateSection = ({ showTemplateSection }) => {
  const { t } = useTranslate("templatebanner");
  const lang = useSelector((state) => state.language.lang);
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authSlice);
  const [showRoleSwitchModal, setShowRoleSwitchModal] = useState(false);
  const [updateUser, { isLoading: isLoadingUpdate }] = useUpdateUserMutation();
  const {
    data: templateData,
    isLoading: isTemplateLoading,
    refetch: refetchTemplates,
  } = useGetAllTemplatesQuery(
    { featured: true, status: "approved", lang, page: 1, rowsPerPage: 7 },
    { refetchOnMountOrArgChange: true },
  );

  useEffect(() => {
    refetchTemplates();
  }, []);

  const featuredTemplates = templateData?.data || [];
  if (!showTemplateSection) return null;

  const handleSwitchRole = async () => {
    try {
      const payload = { role: "both" };

      await updateUser({
        id: user?._id,
        payload,
        params: { lang },
      }).unwrap();

      toast.success(t("toast.roleUpdateSuccess"));
      setShowRoleSwitchModal(false);

      refetchTemplates();
    } catch (err) {
      toast.error(err?.data?.message || t("toast.roleUpdateError"));
    }
  };

  const handleClick = () => {
    if (!user) {
      toast.info(t("toast.loginRequiredCustom"));
    }
    if (user?.role === "learner") {
      setShowRoleSwitchModal(true);
    }
    if (user?.role === "creator" || user?.role === "both") {
      return navigate("/create-custom-template");
    }
  };

  return (
    <section
      className={`${
        (user && user.country) || localStorage?.getItem("selectedCountry")
          ? "banner"
          : "top-categories"
      }`}
    >
      <div className="container-fluid text-center">
        <h1 className="gallery-title mb-3 text-uppercase text-white">
          {t("title")}
        </h1>
        <p className="mb-4 p-lg l-grey-text">{t("subtitle")}</p>
        <button onClick={() => navigate("/template")} className="site-btn mb-5">
          {t("continueBtn")}
        </button>

        {isTemplateLoading ? (
          <SkeletonCourseCard length={7} />
        ) : (
          <OwlCarousel
            className="category-carousel"
            options={{
              loop: false,
              rewind: true,
              margin: 10,
              nav: true,
              dots: false,
              lazyLoad: true,
              autoplay: true,
              autoplayTimeout: 5000,
              autoplayHoverPause: true,
              smartSpeed: 500,
              navText: [
                '<i class="fas fa-angle-left"></i>',
                '<i class="fas fa-angle-right"></i>',
              ],
              responsive: {
                0: { items: 2, stagePadding: 0, autoplay: false },
                576: { items: 2, stagePadding: 20, autoplay: true },
                768: { items: 3, stagePadding: 0, autoplay: true },
                992: { items: 4, stagePadding: 0, autoplay: true },
                1200: { items: 5, stagePadding: 0, autoplay: true },
              },
            }}
          >
            {/* Custom Template Card */}
            <div className="template-card">
              <img
                src={images.home.template_default}
                alt={t("customtitle")}
                className="card-image"
              />
              <div className="card-body text-center">
                <p
                  style={{
                    position: "relative",
                    display: "inline-block",
                    marginBottom: "0.5rem",
                  }}
                >
                  <i
                    className="fas fa-pen"
                    style={{ fontSize: "32px", color: "#6C2BD9" }}
                  ></i>
                  <i
                    className="fas fa-star"
                    style={{
                      color: "#6C2BD9",
                      position: "absolute",
                      top: "-5px",
                      left: "-8px",
                      fontSize: "14px",
                    }}
                  ></i>
                </p>
                <h5
                  className="card-title text-truncate-2"
                  style={{ fontSize: "22px" }}
                >
                  {t("customtitle")}
                </h5>
                <p className="card-subtitle text-truncate-2">
                  {t("customsubtitle")}
                </p>
                <button className="site-btn" onClick={handleClick}>
                  {t("custombtn")}
                </button>
              </div>
            </div>

            {featuredTemplates?.map((item, idx) => (
              <div key={idx} className="item">
                <HeroSectionTemplateCard {...item} />
              </div>
            ))}
          </OwlCarousel>
        )}
      </div>

      <SwitchToBothRoleModal
        show={showRoleSwitchModal}
        close={() => setShowRoleSwitchModal(false)}
        onConfirm={handleSwitchRole}
        isLoading={isLoadingUpdate}
        title={t("switchRoleModal.title")}
        subtitle={t("switchRoleModal.subtitle")}
        paragraph1={t("switchRoleModal.paragraph1")}
        paragraph2={t("switchRoleModal.paragraph2")}
        roleText={t("switchRoleModal.role")}
        yesText={t("switchRoleModal.yesBtn")}
        cancelText={t("switchRoleModal.cancelBtn")}
      />
    </section>
  );
};

export default TemplateSection;
